import { MouseEventHandler } from 'react'

import { useDrag, useDrop } from 'react-dnd'

import CloseIcon from '@/assets/icons/icon-close.svg'
import { useHasChatUNreadMessages } from '@/models/chattingUI/chats'
import { useChattingTabs } from '@/modules/chatting/state/tabs-provider'
import { ChattingTab } from '@/modules/chatting/types'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { classNames } from '@/utils/helpers'

interface Props {
  tab: ChattingTab
  index: number
  onClick: () => void
  setActiveElementRef: (ref: HTMLDivElement | null) => void
}

export const MobileTabItem = ({
  tab,
  index,
  onClick,
  setActiveElementRef,
}: Props) => {
  const { moveTabs, tabs, closeTab, activeTabIndex } = useChattingTabs()
  const { fanId, avatar, title } = tab

  const [, dropRef] = useDrop({
    accept: 'tab',
    drop: (origin: ChattingTab) => moveTabs(origin, tab),
  })

  const [, dragRef, previewRef] = useDrag({
    item: () => tab,
    type: 'tab',
  })

  const handleClose: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault()
    closeTab(index)
  }

  const isActive = activeTabIndex === index
  const isCloseDisabled = index === 0 && tabs.length === 1
  const hasTabUnreadMessage = useHasChatUNreadMessages(tab.fanId)

  return (
    <div
      ref={(ref) => {
        dragRef(ref)
        dropRef(ref)
        previewRef(ref)
        setActiveElementRef(ref)
      }}
      onClick={onClick}
      className={classNames(
        'group/item relative flex w-full flex-1 cursor-pointer items-center justify-between rounded-lg border border-transparent p-2 text-left hover:bg-primary-50 dark:hover:bg-secondary-800',
        isActive && 'border-primary-500 bg-primary-50 dark:bg-secondary-800',
      )}
    >
      <div className="flex items-center gap-3">
        {fanId > 0 ? (
          <div className="relative h-max w-max rounded-full">
            <CreatorAvatar
              avatarSrc={avatar}
              size={40}
              name={title}
              className="shrink-0"
            />
            {hasTabUnreadMessage && (
              <div className="bg-primary-500 absolute right-[1px] top-[1px] h-2 w-2 rounded-full" />
            )}
          </div>
        ) : (
          <div className="bg-secondary-400 h-10 w-10 shrink-0 rounded-full" />
        )}
        <span className="font-medium">{title}</span>
      </div>
      <IconButton
        icon={CloseIcon}
        disabled={isCloseDisabled}
        iconClassName="h-6 w-6"
        onClick={handleClose}
      />
    </div>
  )
}
